const INVOICE_TASK_STATE_FIELDS = ["checked", "taskTime", "identityId"];

const hasOwn = (obj, field) => (
	obj && Object.prototype.hasOwnProperty.call(obj, field)
);

const toTrimmedString = (value) => (
	value === undefined || value === null ? "" : String(value).trim()
);

const getLineId = (item = {}) => toTrimmedString(item.lineId);

const getFallbackKeys = (item = {}) => {
	const id = toTrimmedString(item.id);
	const productId = toTrimmedString(item.productId);
	const description = toTrimmedString(item.description);
	const settlesContractLineId = toTrimmedString(item.settlesContractLineId);
	const keys = [];

	if (id && description) {
		keys.push(`id:${id}|description:${description}|settles:${settlesContractLineId}`);
	}
	if (productId && description) {
		keys.push(`productId:${productId}|description:${description}|settles:${settlesContractLineId}`);
	}

	return keys;
};

export const hasInvoiceTaskState = (item = {}) => (
	INVOICE_TASK_STATE_FIELDS.some((field) => hasOwn(item, field))
);

const getInvoiceTaskState = (item = {}) => {
	const taskState = {};

	INVOICE_TASK_STATE_FIELDS.forEach((field) => {
		if (hasOwn(item, field)) {
			taskState[field] = item[field];
		}
	});
	if (taskState.checked === false) {
		taskState.taskTime = 0;
		taskState.identityId = "";
	}

	return taskState;
};

const buildInvoiceTaskStateLookup = (sourceItems = []) => {
	const lineIdTaskState = new Map();
	const fallbackCandidates = new Map();

	(Array.isArray(sourceItems) ? sourceItems : []).forEach((item) => {
		if (!item || typeof item !== "object" || !hasInvoiceTaskState(item)) {
			return;
		}

		const taskState = getInvoiceTaskState(item);
		const lineId = getLineId(item);

		if (lineId) {
			lineIdTaskState.set(lineId, taskState);
		}

		getFallbackKeys(item).forEach((key) => {
			const candidates = fallbackCandidates.get(key) || [];
			candidates.push(taskState);
			fallbackCandidates.set(key, candidates);
		});
	});

	const fallbackTaskState = new Map();
	fallbackCandidates.forEach((candidates, key) => {
		if (candidates.length === 1) {
			fallbackTaskState.set(key, candidates[0]);
		}
	});

	return {
		lineIdTaskState,
		fallbackTaskState
	};
};

export const mergeInvoiceItemTaskState = (invoiceItems = [], sourceItems = []) => {
	if (!Array.isArray(invoiceItems)) {
		return invoiceItems;
	}

	const { lineIdTaskState, fallbackTaskState } = buildInvoiceTaskStateLookup(sourceItems);

	if (lineIdTaskState.size === 0 && fallbackTaskState.size === 0) {
		return invoiceItems;
	}

	return invoiceItems.map((item) => {
		if (!item || typeof item !== "object") {
			return item;
		}

		const lineId = getLineId(item);
		let taskState = lineId ? lineIdTaskState.get(lineId) : null;

		if (!taskState) {
			const fallbackKey = getFallbackKeys(item).find((key) => fallbackTaskState.has(key));
			taskState = fallbackKey ? fallbackTaskState.get(fallbackKey) : null;
		}

		return taskState ? { ...item, ...taskState } : item;
	});
};

const getEstimateValue = (estimate = {}) => toTrimmedString(estimate.value);

const getEstimateInvoiceItems = (pricingState = {}, estimateValue = "") => {
	const estimates = Array.isArray(pricingState.estimates) ? pricingState.estimates : [];
	const estimate = estimates.find((entry) => getEstimateValue(entry) === estimateValue);
	return Array.isArray(estimate?.invoiceItems) ? estimate.invoiceItems : [];
};

const getDocumentInvoiceItems = (pricingContractProto = null, documentId = "") => {
	const documents = Array.isArray(pricingContractProto?.documents) ? pricingContractProto.documents : [];
	const document = documents.find((entry) => toTrimmedString(entry?.id) === documentId);
	return Array.isArray(document?.invoiceItems) ? document.invoiceItems : [];
};

const getActiveDocumentInvoiceItems = (pricingContractProto = null) => {
	const documents = Array.isArray(pricingContractProto?.documents) ? pricingContractProto.documents : [];
	const activeDocumentId = toTrimmedString(pricingContractProto?.activeDocumentId);
	const activeDocument = documents.find((entry) => toTrimmedString(entry?.id) === activeDocumentId)
		|| documents[documents.length - 1];
	return Array.isArray(activeDocument?.invoiceItems) ? activeDocument.invoiceItems : [];
};

export const resolveFullestActiveInvoiceItems = (pricingState = {}, fallbackItems = []) => {
	const selectedValue = toTrimmedString(pricingState.selectedValue);
	const candidates = [
		getActiveDocumentInvoiceItems(pricingState.pricingContractProto),
		getEstimateInvoiceItems(pricingState, selectedValue),
		Array.isArray(pricingState.invoiceItems) ? pricingState.invoiceItems : [],
		Array.isArray(fallbackItems) ? fallbackItems : []
	];

	return candidates.reduce((bestItems, candidateItems) => (
		Array.isArray(candidateItems) && candidateItems.length > bestItems.length
			? candidateItems
			: bestItems
	), []);
};

export const mergePricingInvoiceTaskState = (pricingState = {}, sourcePricingState = {}) => {
	const result = { ...(pricingState || {}) };
	const selectedValue = toTrimmedString(sourcePricingState.selectedValue) || toTrimmedString(pricingState.selectedValue);
	const sourceInvoiceItems = Array.isArray(sourcePricingState.invoiceItems) ? sourcePricingState.invoiceItems : [];

	if (Array.isArray(result.invoiceItems)) {
		result.invoiceItems = mergeInvoiceItemTaskState(result.invoiceItems, sourceInvoiceItems);
	}

	if (Array.isArray(result.estimates)) {
		result.estimates = result.estimates.map((estimate) => {
			if (!estimate || typeof estimate !== "object") {
				return estimate;
			}

			const estimateValue = getEstimateValue(estimate);
			const sourceItems = getEstimateInvoiceItems(sourcePricingState, estimateValue);
			const selectedSourceItems = estimateValue && estimateValue === selectedValue
				? [...sourceItems, ...sourceInvoiceItems]
				: sourceItems;

			return {
				...estimate,
				invoiceItems: mergeInvoiceItemTaskState(estimate.invoiceItems || [], selectedSourceItems)
			};
		});
	}

	if (result.pricingContractProto && Array.isArray(result.pricingContractProto.documents)) {
		const activeDocumentId = toTrimmedString(result.pricingContractProto.activeDocumentId);

		result.pricingContractProto = {
			...result.pricingContractProto,
			documents: result.pricingContractProto.documents.map((document) => {
				if (!document || typeof document !== "object") {
					return document;
				}

				const documentId = toTrimmedString(document.id);
				const sourceItems = getDocumentInvoiceItems(sourcePricingState.pricingContractProto, documentId);
				const activeSourceItems = documentId && documentId === activeDocumentId
					? [...sourceItems, ...sourceInvoiceItems]
					: sourceItems;

				return {
					...document,
					invoiceItems: mergeInvoiceItemTaskState(document.invoiceItems || [], activeSourceItems)
				};
			})
		};
	}

	return result;
};
